import readHouseholdField, { HOUSEHOLD_FIELDS } from './householdFields'

/**
 * @typedef {object} StreetParts
 * @property {string} [Straatnaam] - Street name as stored in PersonaData
 * @property {string} [Huisnummer] - House number as stored in PersonaData
 */

/**
 * Groups PersonaData rows into `{ [addressId]: { <titel>: waarde, <vast veld>: waarde } }`.
 *
 * Elke eigen gegevensregel staat er onder zijn titel in (voor de extra kolommen
 * in het overzicht). Daaroverheen komen de VASTE gegevens (`Naam`, `Telefoon`,
 * `Straatnaam`, `Huisnummer`, `Toevoeging`, …) zoals `readHouseholdField` ze
 * leest: het veld op het huishouden, en tot de migratie de oude regel. Geef de
 * huishoudens mee, anders telt alleen de oude regel.
 * AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T6)
 *
 * @param {Array<{PersonaID?: string, Title?: string, Value?: string}>} [personaDataRows]
 * @param {Array<{ID?: string}>} [adresses] - De `Adresses`-documenten
 * @returns {Record<string, StreetParts>}
 */
const buildStreetLabelMap = (personaDataRows, adresses = []) => {
    /** @type {Record<string, Record<string, *>>} */
    const map = {}
    /** @type {Record<string, Array<object>>} */
    const rowsById = {}

    ;(Array.isArray(personaDataRows) ? personaDataRows : []).forEach((row) => {
        if (!row?.PersonaID) return
        if (!map[row.PersonaID]) map[row.PersonaID] = {}
        map[row.PersonaID][row.Title] = row.Value
        ;(rowsById[row.PersonaID] = rowsById[row.PersonaID] || []).push(row)
    })

    const adressById = {}
    ;(Array.isArray(adresses) ? adresses : []).forEach((adress) => {
        if (adress?.ID) adressById[adress.ID] = adress
    })

    new Set([...Object.keys(map), ...Object.keys(adressById)]).forEach((id) => {
        const adress = adressById[id] || { ID: id }
        const rows = rowsById[id] || []
        map[id] = { ...(map[id] || {}) }
        Object.keys(HOUSEHOLD_FIELDS).forEach((field) => {
            map[id][field] = readHouseholdField(adress, rows, field)
        })
    })

    return map
}

/**
 * Huisnummer met toevoeging zoals op de deur: een letter direct erachter
 * ('12A'), een cijfer met een streepje ('12-1'), want '121' is een ander huis.
 * @param {string} [nummer]
 * @param {string} [toevoeging]
 * @returns {string}
 */
const doorNumber = (nummer, toevoeging) => {
    if (!nummer) return ''
    if (!toevoeging) return nummer
    return /^\d/.test(toevoeging) ? `${nummer}-${toevoeging}` : `${nummer}${toevoeging}`
}

/**
 * Renders "Straatnaam Huisnummer" for one address, or an em dash when unknown.
 *
 * The em dash is a DISPLAY fallback. Pass `placeholder: ''` when the result is
 * used as a sort key — to a collator '—' is ordinary text that sorts before
 * letters, which would drag every address without a street to the top.
 *
 * AI-CONTEXT: See docs/pitfalls.md#ui-display-fallback-used-as-sort-key
 *
 * @param {Record<string, StreetParts>} map - Output of buildStreetLabelMap
 * @param {string} addressId - The Adresses.ID to look up
 * @param {string} [placeholder] - Shown when nothing is known; pass '' for sorting
 * @returns {string}
 */
const streetLabel = (map, addressId, placeholder = '—') => {
    const entry = (map || {})[addressId] || {}
    const label = [entry.Straatnaam, doorNumber(entry.Huisnummer, entry.Toevoeging)].filter(Boolean).join(' ').trim()
    return label || placeholder
}

/**
 * Weergavenaam van één huishouden voor keuzelijsten en paginakoppen:
 * straat + huisnummer uit PersonaData, met de legacy `Adresses.Title` en
 * daarna een placeholder als terugval.
 *
 * `placeholder` werkt zoals bij `streetLabel`: geef `''` mee wanneer de
 * uitkomst een sorteersleutel of een zoekwaarde wordt. 'Naamloos huishouden' is
 * gewone tekst voor een collator en zou die rijen tussen de N'en zetten.
 * AI-CONTEXT: See docs/pitfalls.md#ui-display-fallback-used-as-sort-key
 *
 * @param {Record<string, StreetParts>} map - Output of buildStreetLabelMap
 * @param {{ID?: string, Title?: string}} adress - Het Adresses-document
 * @param {string} [placeholder] - Wat er staat als er niets bekend is; '' voor sorteren en zoeken
 * @returns {string}
 */
const householdLabel = (map, adress, placeholder = 'Naamloos huishouden') => {
    const street = streetLabel(map, adress?.ID, '')
    if (street) return street
    return (adress?.Title || '').trim() || placeholder
}

/**
 * One definition of "the household's name". Households store it as a single
 * `Naam` field, but persona-style records split it — both shapes occur in the
 * same collection, so every reader needs the same fallback chain.
 *
 * Shared with the housevisit confirmation e-mail in `Tasks.jsx`; don't inline
 * a second copy.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 */
const HOUSEHOLD_NAME_FIELD = {
    source: ['Voornaam', 'Achternaam'],
    fallback: ['Naam'],
    join: ' ',
}

/**
 * Renders the resident's name for one household, or an em dash when unknown.
 * De naam is het vaste veld `Naam` uit de map (`readHouseholdField`: het
 * huishouden, anders Voornaam + Achternaam, anders Naam uit de oude regels).
 *
 * The dash matters: a household without a name has to stay visible and
 * sortable in the overview. A blank cell reads as "nothing recorded", a
 * missing row reads as "deleted".
 *
 * It is a DISPLAY fallback only. Pass `placeholder: ''` when the result is used
 * as a sort key — a collator treats '—' as ordinary text and sorts it before
 * letters, so nameless households would land at the top instead of the bottom.
 *
 * @param {Record<string, StreetParts>} map - Output of buildStreetLabelMap
 * @param {string} addressId - The Adresses.ID to look up
 * @param {string} [placeholder] - Shown when nothing is known; pass '' for sorting
 * @returns {string}
 */
const nameLabel = (map, addressId, placeholder = '—') => {
    const entry = (map || {})[addressId] || {}
    return String(entry.Naam || '').trim() || placeholder
}

/**
 * De titels waaronder een telefoonnummer in PersonaData kan staan, op volgorde
 * van voorkeur. Eén lijst, gedeeld met de SMS-herinneringen en met de queries
 * die deze rijen ophalen — een lezer die er één mist, laat een huishouden
 * onbelbaar lijken terwijl het nummer er wel staat.
 *
 * Houd de lijst kort: hij gaat in een Firestore `in`-filter.
 */
const HOUSEHOLD_PHONE_TITLES = ['Telefoonnummer', 'Telefoon', 'Mobiel']

/**
 * Rendert het telefoonnummer van één huishouden, of een em-streepje.
 *
 * Het vaste veld `Telefoon` uit de map (`readHouseholdField`).
 * Anders dan bij de naam worden de titels NIET samengevoegd maar op volgorde
 * afgelopen: twee gevulde velden zijn twee nummers, en die achter elkaar
 * plakken levert een nummer op dat niet bestaat.
 *
 * Net als bij `streetLabel` is het streepje een WEERGAVE-terugval. Geef
 * `placeholder: ''` mee wanneer de uitkomst een belknop moet aansturen of als
 * sorteersleutel dient.
 *
 * @param {Record<string, StreetParts>} map - Output of buildStreetLabelMap
 * @param {string} addressId - The Adresses.ID to look up
 * @param {string} [placeholder] - Shown when nothing is known; pass '' for a call button
 * @returns {string}
 */
const phoneLabel = (map, addressId, placeholder = '—') => {
    const entry = (map || {})[addressId] || {}
    return String(entry.Telefoon || '').trim() || placeholder
}

export {
    buildStreetLabelMap,
    streetLabel,
    householdLabel,
    nameLabel,
    phoneLabel,
    HOUSEHOLD_NAME_FIELD,
    HOUSEHOLD_PHONE_TITLES,
}
